import React, { useEffect, useMemo, useRef, useState } from "react";
import { searchCountryAuthorities } from "../api/nft";
import { useTranslation } from "react-i18next";

const MIN_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 220;

export function CountryAuthorityPicker({
  id,
  value,
  selectedAuthority,
  gradingSystem,
  onChangeValue,
  onAuthoritySelect,
  onClearAuthority,
  onBlur,
  disabled = false,
  placeholder,
  className = "cert-input",
}) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [loadError, setLoadError] = useState("");
  const rootRef = useRef(null);
  const activeQuery = useMemo(() => String(value || "").trim(), [value]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!rootRef.current) return;
      if (rootRef.current.contains(event.target)) return;
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  useEffect(() => {
    if (disabled) {
      setItems([]);
      setIsOpen(false);
      setIsLoading(false);
      setLoadError("");
      return undefined;
    }
    if (!isOpen) {
      return undefined;
    }
    if (!activeQuery || activeQuery.length < MIN_QUERY_LENGTH) {
      setItems([]);
      setLoadError("");
      setIsLoading(false);
      return undefined;
    }

    let isMounted = true;
    const timer = setTimeout(async () => {
      setIsLoading(true);
      setLoadError("");
      try {
        const response = await searchCountryAuthorities({
          query: activeQuery,
          gradingSystem,
          limit: 20,
        });
        if (!isMounted) return;
        const nextItems = Array.isArray(response?.data?.items) ? response.data.items : [];
        setItems(nextItems);
      } catch (error) {
        if (!isMounted) return;
        setItems([]);
        setLoadError(t("cert.fields.country.searchError", { defaultValue: "Unable to load options." }));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [activeQuery, disabled, gradingSystem, isOpen, t]);

  const handleInputChange = (event) => {
    const nextValue = event.target.value;
    onChangeValue(nextValue);
    if (selectedAuthority?.code && nextValue !== (selectedAuthority.display_name || "")) {
      handleClearSelection();
    }
    setIsOpen(true);
    setLoadError("");
  };

  const handleSelect = (item) => {
    onChangeValue(item?.display_name || "");
    onAuthoritySelect(item);
    setItems([]);
    setLoadError("");
    setIsOpen(false);
  };

  const handleClearSelection = () => {
    if (typeof onClearAuthority === "function") {
      onClearAuthority();
      return;
    }
    onAuthoritySelect(null);
  };

  const renderMenu = () => {
    if (!isOpen || disabled) return null;
    if (!activeQuery || activeQuery.length < MIN_QUERY_LENGTH) {
      return (
        <div className="country-authority-picker__menu country-authority-picker__menu--hint">
          {t("cert.fields.country.searchMinChars", {
            defaultValue: "Type at least {{count}} characters",
            count: MIN_QUERY_LENGTH,
          })}
        </div>
      );
    }
    if (isLoading) {
      return (
        <div className="country-authority-picker__menu country-authority-picker__menu--hint">
          {t("cert.fields.country.searchLoading", { defaultValue: "Searching..." })}
        </div>
      );
    }
    if (loadError) {
      return <div className="country-authority-picker__menu country-authority-picker__menu--error">{loadError}</div>;
    }
    if (!items.length) {
      return (
        <div className="country-authority-picker__menu country-authority-picker__menu--hint">
          {t("cert.fields.country.searchEmpty", {
            defaultValue: "No matches. The original slab text will still be saved.",
          })}
        </div>
      );
    }
    return (
      <ul className="country-authority-picker__menu country-authority-picker__menu--list" role="listbox">
        {items.map((item) => {
          const code = String(item.code || "");
          const isSelected = code && selectedAuthority?.code === code;
          return (
            <li key={code}>
              <button
                type="button"
                className={`country-authority-picker__option ${isSelected ? "country-authority-picker__option--selected" : ""}`}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => handleSelect(item)}
                data-testid="country-authority-option"
              >
                <span className="country-authority-picker__name">{item.display_name}</span>
                <span className="country-authority-picker__meta">{item.period}</span>
              </button>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className="country-authority-picker" ref={rootRef}>
      <input
        id={id}
        value={value}
        onChange={handleInputChange}
        onFocus={() => setIsOpen(true)}
        onBlur={onBlur}
        type="text"
        placeholder={placeholder || t("cert.fields.country.placeholder", { defaultValue: "Search and select" })}
        className={className}
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={isOpen}
        disabled={disabled}
        maxLength={64}
      />
      {renderMenu()}
    </div>
  );
}
